import React, { useEffect, useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { useAuth } from '../../hooks/useAuth';
import { isSuperAdmin } from '../../utils/roles';
import {
  listV2BillableItems,
  createV2BillableItem,
  updateV2BillableItem,
  deleteV2BillableItem,
  listV2StripeMeters,
  type V2BillableItem,
  type V2StripeMeter,
} from '../../api/billingProducts';

const fmt = (cents: number, currency = 'usd') =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(cents / 100);

interface FormState {
  id?: string;
  name: string;
  description: string;
  unit_amount_dollars: string; // input as dollars, converted to cents on submit
  currency: string;
  interval: 'month' | 'year' | 'week' | 'day';
  usage_type: 'licensed' | 'metered';
  meter_key: string;
  enabled: boolean;
}

const EMPTY_FORM: FormState = {
  name: '',
  description: '',
  unit_amount_dollars: '0.00',
  currency: 'usd',
  interval: 'month',
  usage_type: 'licensed',
  meter_key: '',
  enabled: true,
};

export const BillableItemsManager: React.FC = () => {
  const { user } = useAuth();
  const isSuper = isSuperAdmin((user?.role ?? '') as any);
  const [items, setItems] = useState<V2BillableItem[]>([]);
  const [meters, setMeters] = useState<V2StripeMeter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const [its, mts] = await Promise.all([
        listV2BillableItems(true).catch((e) => { throw new Error(`Items: ${e.message}`); }),
        listV2StripeMeters().catch(() => [] as V2StripeMeter[]),
      ]);
      setItems(its);
      setMeters(mts);
    } catch (e: any) {
      setError(e.message ?? String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const startCreate = () => setEditing({ ...EMPTY_FORM });

  const startEdit = (it: V2BillableItem) => setEditing({
    id: it.id,
    name: it.name,
    description: it.description ?? '',
    unit_amount_dollars: (it.unit_amount_cents / 100).toFixed(2),
    currency: it.currency,
    interval: it.interval,
    usage_type: it.usage_type,
    meter_key: it.meter_key ?? '',
    enabled: it.enabled,
  });

  const submit = async () => {
    if (!editing) return;
    setSaving(true);
    setError(null);
    try {
      const body = {
        name: editing.name,
        description: editing.description || undefined,
        unit_amount_cents: Math.round(parseFloat(editing.unit_amount_dollars || '0') * 100),
        currency: editing.currency,
        interval: editing.interval,
        usage_type: editing.usage_type,
        meter_key: editing.usage_type === 'metered' ? editing.meter_key : undefined,
        enabled: editing.enabled,
      };
      if (editing.id) {
        await updateV2BillableItem(editing.id, body);
      } else {
        await createV2BillableItem(body);
      }
      setEditing(null);
      await refresh();
    } catch (e: any) {
      setError(e.message ?? String(e));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (it: V2BillableItem) => {
    try {
      await updateV2BillableItem(it.id, { enabled: !it.enabled });
      await refresh();
    } catch (e: any) {
      setError(e.message ?? String(e));
    }
  };

  const archive = async (it: V2BillableItem) => {
    if (!window.confirm(`Archive "${it.name}"? This soft-deletes the item and disables the Stripe Product/Price.`)) return;
    try {
      await deleteV2BillableItem(it.id);
      await refresh();
    } catch (e: any) {
      setError(e.message ?? String(e));
    }
  };

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-gray-900">Billable Items</h3>
          <p className="text-sm text-gray-500">Source of truth: changes mirror to Stripe automatically.{isSuper ? '' : ' Read-only for your role.'}</p>
        </div>
        {isSuper && <Button onClick={startCreate}>+ New billable item</Button>}
      </div>

      {error && (
        <div className="mb-3 px-3 py-2 rounded bg-red-50 text-red-700 text-sm border border-red-200">{error}</div>
      )}

      {loading ? (
        <div className="text-gray-500 text-sm">Loading...</div>
      ) : items.length === 0 ? (
        <div className="text-gray-500 text-sm">No billable items yet. Create one to start billing.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-gray-500 border-b">
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Meter</th>
                <th className="px-3 py-2 text-right">Price</th>
                <th className="px-3 py-2">Interval</th>
                <th className="px-3 py-2">Stripe</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className={`border-b hover:bg-gray-50 ${it.archived_at ? 'opacity-50' : ''}`}>
                  <td className="px-3 py-2">
                    <div className="font-medium">{it.name}</div>
                    {it.description && <div className="text-xs text-gray-500">{it.description}</div>}
                  </td>
                  <td className="px-3 py-2">
                    <Badge color={it.usage_type === 'metered' ? 'purple' : 'blue'}>{it.usage_type}</Badge>
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600">{it.meter_key || '—'}</td>
                  <td className="px-3 py-2 text-right">{fmt(it.unit_amount_cents, it.currency)}</td>
                  <td className="px-3 py-2">/{it.interval}</td>
                  <td className="px-3 py-2 text-xs text-gray-500">
                    {it.stripe_product_id ? (
                      <span className="font-mono">{it.stripe_product_id.slice(0, 14)}…</span>
                    ) : '—'}
                  </td>
                  <td className="px-3 py-2">
                    {it.archived_at ? (
                      <Badge color="gray">archived</Badge>
                    ) : isSuper ? (
                      <button onClick={() => toggle(it)} className={`text-xs px-2 py-0.5 rounded-full font-medium ${it.enabled ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}>
                        {it.enabled ? 'enabled' : 'disabled'}
                      </button>
                    ) : (
                      <Badge color={it.enabled ? 'green' : 'gray'}>{it.enabled ? 'enabled' : 'disabled'}</Badge>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {isSuper && !it.archived_at && (
                      <>
                        <Button variant="secondary" onClick={() => startEdit(it)}>Edit</Button>
                        <button className="ml-2 text-red-600 text-xs hover:underline" onClick={() => archive(it)}>Archive</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing?.id ? 'Edit billable item' : 'New billable item'}>
        {editing && (
          <div className="space-y-3">
            <Input label="Name" value={editing.name} onChange={(e: any) => setEditing({ ...editing, name: e.target.value })} />
            <Input label="Description" value={editing.description} onChange={(e: any) => setEditing({ ...editing, description: e.target.value })} />
            <div className="grid grid-cols-2 gap-3">
              <Input label="Price (USD)" type="number" step="0.01" min="0" value={editing.unit_amount_dollars} onChange={(e: any) => setEditing({ ...editing, unit_amount_dollars: e.target.value })} />
              <div>
                <label className="block text-xs uppercase text-gray-500 mb-1">Interval</label>
                <select value={editing.interval} onChange={(e) => setEditing({ ...editing, interval: e.target.value as any })} className="w-full border border-gray-300 rounded px-2 py-1.5">
                  <option value="month">Month</option>
                  <option value="year">Year</option>
                  <option value="week">Week</option>
                  <option value="day">Day</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs uppercase text-gray-500 mb-1">Type</label>
              <div className="flex gap-2">
                {(['licensed', 'metered'] as const).map((t) => (
                  <button
                    key={t}
                    onClick={() => setEditing({ ...editing, usage_type: t })}
                    className={`px-3 py-1.5 rounded text-sm font-medium ${editing.usage_type === t ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                    disabled={!!editing.id /* immutable on existing */}
                  >
                    {t}
                  </button>
                ))}
              </div>
              {editing.id && <div className="text-xs text-gray-400 mt-1">Type is immutable on existing items.</div>}
            </div>
            {editing.usage_type === 'metered' && (
              <div>
                <label className="block text-xs uppercase text-gray-500 mb-1">Meter</label>
                <select value={editing.meter_key} onChange={(e) => setEditing({ ...editing, meter_key: e.target.value })} className="w-full border border-gray-300 rounded px-2 py-1.5" disabled={!!editing.id}>
                  <option value="">— pick a meter —</option>
                  {meters.map((m) => (
                    <option key={m.id} value={m.event_name}>
                      {m.display_name || m.event_name} ({m.event_name})
                    </option>
                  ))}
                </select>
                {meters.length === 0 && (
                  <div className="text-xs text-amber-600 mt-1">No meters returned. Create one in Stripe first, or check that this user has super_admin.</div>
                )}
              </div>
            )}
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={editing.enabled} onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })} />
              Enabled (item is active and bills when included on a subscription)
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={() => setEditing(null)}>Cancel</Button>
              <Button onClick={submit} disabled={saving || !editing.name}>{saving ? 'Saving…' : 'Save'}</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};
